import {
  addDefaultProps,
  addDisplayName,
  addHandlers,
  addProps,
  addWrapper,
  flowMax,
  SimpleUnchangedProps,
} from 'ad-hok'
import { branchIfFailsPredicate } from 'ad-hok-utils'
import cx from 'classnames'
import { FC } from 'react'
import { Helmet } from 'react-helmet-async'
import { Trans } from 'react-i18next'
import { createUseStyles } from 'react-jss'
import { Link } from 'react-router-dom'
import { addApi } from 'shared/src/api'
import { addAuthContext } from 'shared/src/utils/auth'
import {
  addDataLoadingFromPromise,
  hasData,
} from 'shared/src/utils/dataLoading'
import { addWindowScrollPositionThrottled } from 'shared/src/utils/scrollPosition'

import { ExploreFeatured } from 'components/explore/ExploreCategory'
import { Product } from 'components/feed/fluid/rodeo/Product'
import { JoinWaitlistOrLaunchCmsButton } from 'components/JoinWaitlistOrLaunchCmsButton'
import { LinkOrButton } from 'components/LinkOrButton'
import { Modal } from 'components/Modal'
import { SearchButton } from 'components/navbar/SearchButton'
import { TermsOfService } from 'components/TermsOfService'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { LAYOUT_MAX_WIDTH } from 'utils/layout'
import { makeSampleProduct } from 'utils/product'
import { makeProductDetailUrl } from 'utils/routes'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import { MODAL_Z_INDEX } from 'utils/zIndex'
import closeIcon from '../../assets/close-icon.svg'
import followedCreatorsChevronIcon from '../../assets/followed-creators-chevron-icon-white.svg'
import loginRegisterTile from '../../assets/login-register-tile.jpg'
import fakeProduct1 from '../../assets/menu-sample-product-1.jpg'
import fakeProduct2 from '../../assets/menu-sample-product-2.jpg'
import rodeoLogo from '../../assets/rodeo-beta-logo.svg'
import seeAllChevronIcon from '../../assets/see-all-chevron-icon.svg'
import { addShowMenuContext } from '../../utils/menu'
import { NavProps } from '../navbar'
import { CreatorFeatures } from './CreatorFeatures'
import { FollowUs } from './FollowUs'
import { LogInOut } from './LogInOut'
import { RodeoDescription } from './RodeoDescription'
import { Subscribe } from './Subscribe'

const useStyles = createUseStyles({
  container: {
    background: '#000000',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100%',
    overflow: 'scroll',
  },
  constrainWidthContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: LAYOUT_MAX_WIDTH,
    flexGrow: 1,
    [`@media(max-width: ${LAYOUT_MAX_WIDTH * 1.25}px)`]: {
      overflowX: 'hidden',
    },
    zIndex: MODAL_Z_INDEX,
    background: '#000000',
  },
  navigationMenu: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 8,
  },
  backContainer: {
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 120,
    height: 37,
  },
  backButtonIcon: {
    height: 44,
    width: 44,
    cursor: 'pointer',
  },
  searchIcon: {
    cursor: 'pointer',
  },
  link: {
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'flex-start',
    marginLeft: 32,
  },
  linkText: {
    color: '#ffffff',
    fontFamily: serifFontFamily,
    fontSize: 22,
    fontWeight: 600,
    lineHeight: '140%',
  },
  linkDescription: {
    color: '#CBA67A',
    fontFamily: sansSerifFontFamily,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '189%',
  },
  linkIconContainer: {
    display: 'flex',
    alignItems: 'center',
    height: 34,
  },
  linkIcon: {
    marginRight: 20,
  },
  textItalic: {
    fontWeight: 700,
    fontStyle: 'italic',
  },
  header: {
    textDecoration: 'none',
    fontFamily: serifFontFamily,
    fontWeight: 700,
    fontSize: 22,
    lineHeight: '23.99px',
    letterSpacing: '0.02em',
    color: '#FFFFFF',
    marginLeft: 28,
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
  },
  subheader: {
    color: '#CBA67A',
    fontFamily: sansSerifFontFamily,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '189%',
    marginLeft: 28,
    marginTop: 2,
  },
  headerMarginBottom: {
    marginBottom: 16,
  },
  headerItalic: {
    fontWeight: 700,
    fontStyle: 'italic',
  },
  bookmarksHeaderContainer: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'space-between',
  },
  bookmarksContainer: {
    display: 'flex',
    marginBottom: 32,
    marginLeft: 31,
    gap: 16,
    overflowX: 'scroll',
    minHeight: 166,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  product: {
    minWidth: 120,
  },
  placeholderLoginImage: {
    height: 165,
  },
  placeholderProduct: {
    opacity: 0.3,
    pointerEvents: 'none',
  },
  seeAll: {
    fontSize: 12,
    fontFamily: sansSerifFontFamily,
    fontWeight: 700,
    lineHeight: '189%',
    color: 'rgba(255, 255, 255, 0.8)',
    textDecoration: 'none',
    marginRight: 24,
  },
  seeAllChevronIcon: {
    marginLeft: 6,
    height: 10,
  },
  discover: {
    marginLeft: 24,
    marginRight: 20,
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontFamily: serifFontFamily,
    fontStyle: 'italic',
    fontWeight: 800,
    fontSize: 32,
    lineHeight: '140%',
    letterSpacing: '0.01em',
    color: 'rgba(255, 255, 255, 0.24)',
  },
  textContainer: {
    position: 'absolute',
    top: '13px',
    left: '0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: '1',
    padding: '5px',
    color: 'white',
  },
  tags: {
    paddingLeft: 50,
  },
  categories: {
    paddingLeft: 80,
    marginBottom: 40,
  },
  creatorFeatures: {},
  description: {
    marginBottom: 71,
  },
  waitlist: {
    marginLeft: 40,
    marginRight: 40,
    marginBottom: 60,
  },
  subscribe: {
    marginBottom: 50,
  },
  followUs: {
    marginBottom: 37,
  },
  logInOut: {
    marginBottom: 20,
  },
  termsOfService: {
    marginLeft: 40,
    marginRight: 40,
  },
})

interface NavItemProps {
  onClick?: () => void
  to: string
  titleTranslationKey: string
  descriptionTranslationKey?: string
  icon: string
  showBackButton?: boolean
}

export const NavItem: FC<NavItemProps> = flowMax(
  addDisplayName('NavItem'),
  addDefaultProps({
    showBackButton: false,
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    onClick,
    to,
    icon,
    showBackButton,
    titleTranslationKey,
    descriptionTranslationKey,
    classes,
    t,
  }) => (
    <Link
      state={{ showBackButton }}
      to={to}
      onClick={onClick}
      className={classes.link}
    >
      <div className={classes.linkIconContainer}>
        <img src={icon} alt="" className={classes.linkIcon} />
      </div>
      <div>
        <div className={classes.linkText}>
          <Trans i18nKey={titleTranslationKey as any} t={t}>
            <span className={classes.textItalic} />
          </Trans>
        </div>
        {descriptionTranslationKey && (
          <div className={classes.linkDescription}>
            {t(descriptionTranslationKey as any)}
          </div>
        )}
      </div>
    </Link>
  )
)

// type AddMenuPage = <TProps extends NavProps>(props: TProps) => TProps

export const addMenuPage: SimpleUnchangedProps = flowMax(
  addShowMenuContext,
  addWrapper((render, { showMenu }) => (
    <>
      {showMenu && <Menu />}
      {render()}
    </>
  ))
)

const addBodyScrollableLayout: SimpleUnchangedProps = flowMax(
  addWrapper((render) => (
    <>
      <Helmet>
        <html className="body-scrollable" />
      </Helmet>
      {render()}
    </>
  ))
)

interface Props extends NavProps {
  standalone?: boolean
}

export const Menu: FC<Props> = flowMax(
  addDisplayName('Menu'),
  addApi,
  addAuthContext,
  addShowMenuContext,
  addDataLoadingFromPromise(
    'data',
    ({ api, isAuthenticated }) =>
      Promise.all([
        api.creatorFeatures.featured(),
        isAuthenticated ? api.follows.creators() : Promise.resolve([]),
        isAuthenticated ? api.getRoundupProducts() : Promise.resolve([]),
        isAuthenticated ? api.users.getCurrent() : Promise.resolve(undefined),
      ]),
    [],
    () => 'menu'
  ),
  addProps(() => ({
    classes: useStyles(),
  })),
  addWrapper((render, { classes }) => (
    <Modal containerClassName={classes.container}>{render()}</Modal>
  )),
  branchIfFailsPredicate('data', hasData),
  addProps(
    ({
      data: {
        data: [creatorFeatures, followedCreators, bookmarks, user],
      },
    }) => ({
      creatorFeatures,
      followedCreators,
      bookmarks,
      user,
    })
  ),
  addWindowScrollPositionThrottled(),
  addProps(({ windowScrollPositionThrottled: { y } }) => ({
    isPageScrolled: y > 0,
  })),
  addBodyScrollableLayout,
  addHandlers({
    onLogin:
      ({ navigateToLogin }) =>
      () => {
        navigateToLogin({ loginTrigger: 'roundup' })
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  addProps(({ t }) => ({
    fakeProducts: [
      makeSampleProduct(
        t('menu.fakeProductOneName'),
        t('menu.fakeProductOneBrand'),
        fakeProduct1
      ),
      makeSampleProduct(
        t('menu.fakeProductTwoName'),
        t('menu.fakeProductTwoBrand'),
        fakeProduct2
      ),
    ],
  })),
  ({
    isAuthenticated,
    followedCreators,
    creatorFeatures,
    bookmarks,
    toggleMenu,
    onLogin,
    fakeProducts,
    user,
    classes,
    t,
  }) => (
    <>
      <Modal containerClassName={classes.container}>
        <div className={classes.constrainWidthContainer}>
          <div className={classes.navigationMenu}>
            <button onClick={toggleMenu} className={classes.backContainer}>
              <img
                src={closeIcon}
                alt={t('back')}
                className={classes.backButtonIcon}
              />
            </button>
            <div className={classes.logoContainer}>
              <img
                src={rodeoLogo}
                alt={t('logoAlt')}
                className={classes.logo}
              />
            </div>
            <SearchButton className={classes.searchIcon} />
          </div>
          <div
            className={cx(
              classes.bookmarksHeaderContainer,
              classes.headerMarginBottom
            )}
          >
            <div>
              <div className={classes.header}>
                <Trans i18nKey="menu.myRoundup" t={t}>
                  <span className={classes.headerItalic} />
                </Trans>
              </div>
              <div className={classes.subheader}>
                {t(
                  isAuthenticated
                    ? 'menu.myRoundupLoggedInDescription'
                    : 'menu.myRoundupLoggedOutDescription'
                )}
              </div>
            </div>
            {isAuthenticated && (
              <LinkOrButton
                navigateTo="/roundup"
                className={classes.seeAll}
                showBackButton
              >
                {t('menu.seeAll')}
                <img
                  src={seeAllChevronIcon}
                  alt=""
                  className={classes.seeAllChevronIcon}
                />
              </LinkOrButton>
            )}
          </div>
          <div className={classes.bookmarksContainer}>
            {bookmarks.length > 0 ? (
              bookmarks.map((product) => (
                <Product
                  product={product}
                  navigateTo={makeProductDetailUrl(product.id)}
                  key={product.id}
                  className={classes.product}
                />
              ))
            ) : (
              <>
                {!isAuthenticated ? (
                  <>
                    <LinkOrButton onClick={onLogin}>
                      <img
                        src={loginRegisterTile}
                        className={classes.placeholderLoginImage}
                        alt={t('menu.loginAlt')}
                      />
                    </LinkOrButton>
                    {fakeProducts.map((product) => (
                      <Product
                        product={product}
                        navigateTo={makeProductDetailUrl(product.id)}
                        key={product.name}
                        className={classes.placeholderProduct}
                      />
                    ))}
                  </>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <Product
                      product={fakeProducts[0]}
                      navigateTo={makeProductDetailUrl(fakeProducts[0].id)}
                      key={fakeProducts[0].name}
                      className={classes.placeholderProduct}
                    />
                    <div className={classes.textContainer}>
                      <div>{t('menu.noProductsInRoundup')}</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <ExploreFeatured
            creators={followedCreators}
            backgroundColor="black"
            renderName={() => (
              <Link
                to="/following"
                className={cx(classes.header, classes.headerMarginBottom)}
              >
                <div>
                  <Trans i18nKey="menu.myCreators" t={t}>
                    <span className={classes.headerItalic} />
                  </Trans>
                </div>
                <img src={followedCreatorsChevronIcon} alt="" />
              </Link>
            )}
            showPlaceholders
          />
          {creatorFeatures.length > 0 && (
            <CreatorFeatures
              creatorFeatures={creatorFeatures}
              className={classes.creatorFeatures}
            />
          )}
          <RodeoDescription className={classes.description} />
          <FollowUs className={classes.followUs} />
          <JoinWaitlistOrLaunchCmsButton
            className={classes.waitlist}
            user={user}
          />
          <Subscribe className={classes.subscribe} />
          <LogInOut className={classes.logInOut} />
          <TermsOfService className={classes.termsOfService} />
        </div>
      </Modal>
    </>
  )
)
